import { Container, Grid } from '@mui/material';
import { useState } from 'react';
import { useParams, useNavigate, useLoaderData } from 'react-router';

import Provision from '../../api/provision';
import DeleteFormModal from '../../components/DeleteFormModal/DeleteFormModal';
import OverviewTable from '../../components/OverviewTable/OverviewTable';
import useApiQuery from '../../hooks/useApiQuery';
import rbacCheck from '../../rbac/rbacCheck';

import EditContactFormModal from './EditContactFormModal';

import { useUserData } from '@/redux/selectors';
import type { APIHookArg } from '@/types';
import type { Contact } from '@/types/contactTypes';
import type { TranslatableLabel } from '@/types/tableTypes';

interface RowItem {
  title: TranslatableLabel;
  value: string | undefined;
}

const lookupType: Record<string, string> = {
  stakeHolder: 'Stakeholder',
  serviceOwner: 'Service Owner',
  tenantOwner: 'Tenant Owner',
  onboardingSupport: 'Onboarding Support',
};

const ContactDetails = (): React.JSX.Element | null => {
  const { contactId, productId, cloudId, juiceGroup, tenant, virtualMachine } = useParams();
  const navigate = useNavigate();
  const roles = useUserData().roles;

  const loaderContact: Contact | null = useLoaderData();
  const [contactDetail, setContactDetail] = useState<Contact | null>(loaderContact);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const { makeApiCall: getContactsCall } = useApiQuery(
    Provision.fetchContacts as unknown as APIHookArg
  );
  const { makeApiCall: deleteContactCall } = useApiQuery(
    Provision.removeContact as unknown as APIHookArg,
    {
      successMessage: 'contactRemoved',
    }
  );
  const canAddEditContacts = rbacCheck(roles, '/products:CONTACTS_PATCH');
  const canDeleteContacts = rbacCheck(roles, '/products:CONTACTS_DELETE');

  const getContactDetails = async (): Promise<void> => {
    const data = (await getContactsCall({ _id: contactId })) as Contact[] | null;
    if (data && data.length > 0) {
      setContactDetail(data[0] ?? null);
    }
  };

  const deleteContact = async (): Promise<void> => {
    setIsDeleteModalOpen(false);
    const data = await deleteContactCall(contactId);

    if (data) {
      if (productId) {
        navigate(`/products/${productId}`);
      } else if (tenant && cloudId && juiceGroup) {
        navigate(`/reports/tenants/${cloudId}/${juiceGroup}/${tenant}`);
      } else if (virtualMachine && cloudId && juiceGroup) {
        navigate(`/reports/hypervisors/${cloudId}/${juiceGroup}/${virtualMachine}`);
      } else {
        navigate('/reports');
      }
    }
  };

  if (!contactDetail?._id) return null;

  const rows: RowItem[] = [
    {
      title: { id: 'name' },
      value: contactDetail.name,
    },
    {
      title: { id: 'role' },
      value: lookupType[contactDetail.type] ?? 'Unknown',
    },
    {
      title: { id: 'phone' },
      value: contactDetail.phoneNumber,
    },
    {
      title: { id: 'slack' },
      value: contactDetail.slackChannel,
    },
  ];

  return (
    <>
      {isEditModalOpen && (
        <EditContactFormModal
          contact={contactDetail}
          onSubmit={() => {
            void getContactDetails();
          }}
          onCancel={() => {
            setIsEditModalOpen(false);
          }}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteFormModal
          fieldName='Contact'
          fieldValue={contactDetail.email}
          onSubmit={() => void deleteContact()}
          onCancel={() => {
            setIsDeleteModalOpen(false);
          }}
        />
      )}
      <Grid
        container
        className='content-with-header'
        direction='column'
        rowGap={4}
      >
        <Grid item>
          <Container>
            <OverviewTable
              header={contactDetail.email}
              rows={rows}
              editAction={{
                onClick: () => {
                  setIsEditModalOpen(true);
                },
                disabled: !canAddEditContacts,
                tooltip: 'contactEditDisabled',
                label: '',
              }}
              deleteAction={{
                onClick: () => {
                  setIsDeleteModalOpen(true);
                },
                disabled: !canDeleteContacts,
                tooltip: 'contactDeleteDisabled',
                label: '',
              }}
            />
          </Container>
        </Grid>
      </Grid>
    </>
  );
};

export default ContactDetails;
